import { useEffect, useMemo, useRef, useState } from "react";
import api, { eur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, AlertTriangle, Loader2, Truck, PackageCheck } from "lucide-react";

const eurN = (v) => (v == null ? "—" : eur(v));

export default function PianoConsegne({ righe, consegnaTipo, defaultZone, provinces = [], valore, piano, onChange, onTotale }) {
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const isRitiro = consegnaTipo === "ritiro";
  const isDedicato = consegnaTipo === "dedicato";
  const plan = piano || [];

  const articoli = useMemo(
    () => (righe || []).filter((r) => r.codice && (+r.quantita || 0) > 0)
      .map((r) => ({ codice: r.codice, descrizione: r.descrizione || r.codice, qta: +r.quantita || 0 })),
    [righe]
  );

  const allocato = useMemo(() => {
    const m = {};
    plan.forEach((c) => (c.righe || []).forEach((rr) => { const k = (rr.codice || "").toLowerCase(); m[k] = (m[k] || 0) + (+rr.qta || 0); }));
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(plan)]);

  const residuoOf = (a) => a.qta - (allocato[a.codice.toLowerCase()] || 0);
  const qtaOf = (idx, codice) => ((plan[idx]?.righe || []).find((r) => r.codice === codice)?.qta) || 0;

  const setConsegna = (idx, patch) => onChange(plan.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  const addConsegna = () => onChange([...plan, { data: "", zone: defaultZone || "", righe: [] }]);
  const removeConsegna = (idx) => onChange(plan.filter((_, i) => i !== idx));
  const setQta = (idx, codice, val) => {
    const art = articoli.find((a) => a.codice === codice);
    const q = Math.max(0, Math.min(+val || 0, art?.qta || 0));
    const rest = (plan[idx]?.righe || []).filter((r) => r.codice !== codice);
    setConsegna(idx, { righe: q > 0 ? [...rest, { codice, qta: q }] : rest });
  };

  const tref = useRef(null);
  useEffect(() => {
    if (!articoli.length) { setRes(null); onTotale && onTotale(0); return; }
    setLoading(true);
    clearTimeout(tref.current);
    tref.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/crm-quotes/piano-consegne/compute", {
          righe, valore_merce: valore || 0, tipo_ordine: "programmato",
          consegna: { tipo: consegnaTipo, provincia: defaultZone, piano: plan.map((c) => ({ data: c.data, zone: c.zone || defaultZone, righe: c.righe || [], prezzo_manuale: +c.prezzo_manuale || 0 })) },
        });
        setRes(data);
        onTotale && onTotale(isRitiro ? 0 : (data.totale || 0));
      } catch { /* silenzioso */ } finally { setLoading(false); }
    }, 500);
    return () => clearTimeout(tref.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(plan), JSON.stringify(articoli), consegnaTipo, defaultZone, valore]);

  const alloc = res?.allocazione;
  const missing = res?.missing_fisici || [];

  return (
    <div className="space-y-3 rounded-lg border border-input bg-muted/20 p-3.5" data-testid="piano-consegne">
      <div className="flex items-center justify-between">
        <div className="text-sm font-bold flex items-center gap-1.5">
          <PackageCheck className="h-4 w-4 text-primary" /> Piano di approvvigionamento e consegna
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        </div>
        <Button type="button" size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={addConsegna} data-testid="piano-add-consegna">
          <Plus className="h-3.5 w-3.5" /> Aggiungi consegna
        </Button>
      </div>

      {!plan.length && <div className="text-xs text-muted-foreground">Nessuna consegna. Aggiungi almeno 2 consegne e assegna gli articoli a ciascuna data.</div>}

      {plan.map((c, idx) => {
        const rout = res?.consegne?.[idx];
        return (
          <div key={idx} className="rounded-md border border-input bg-background/60 p-2.5 space-y-2" data-testid={`piano-consegna-${idx}`}>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-[11px]">Data consegna {idx + 1}</Label>
                <input type="date" value={c.data || ""} onChange={(e) => setConsegna(idx, { data: e.target.value })}
                  className="block h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid={`piano-data-${idx}`} />
              </div>
              {!isRitiro && (
                <div className="min-w-[200px]">
                  <Label className="text-[11px]">Destinazione (provincia)</Label>
                  <Select value={c.zone || defaultZone || ""} onValueChange={(v) => setConsegna(idx, { zone: v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`piano-zona-${idx}`}><SelectValue placeholder="Provincia…" /></SelectTrigger>
                    <SelectContent className="max-h-72">{provinces.map((p) => <SelectItem key={p.sigla} value={p.sigla}>{p.sigla} — {p.nome}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <Button type="button" size="sm" variant="ghost" className="h-8 gap-1 px-2 text-xs text-red-500 hover:text-red-600" onClick={() => removeConsegna(idx)} data-testid={`piano-remove-${idx}`}>
                <Trash2 className="h-3.5 w-3.5" /> Rimuovi
              </Button>
            </div>

            {isDedicato && (
              <div className="flex items-center gap-2" data-testid={`piano-dedicato-${idx}`}>
                <Label className="text-[11px] text-primary font-semibold">Prezzo trasporto dedicato (€)</Label>
                <Input type="number" min="0" step="1" value={c.prezzo_manuale || ""}
                  onChange={(e) => setConsegna(idx, { prezzo_manuale: +e.target.value || 0 })}
                  className="h-7 w-28 text-right text-xs" data-testid={`piano-prezzo-${idx}`} />
              </div>
            )}

            <div className="space-y-1">
              {articoli.map((a) => {
                const res_a = residuoOf(a);
                return (
                  <div key={a.codice} className="grid grid-cols-[1fr_auto_auto] items-center gap-2 text-xs">
                    <span className="truncate" title={a.descrizione}>{a.codice}</span>
                    <span className={`text-[10px] ${res_a === 0 ? "text-emerald-600" : res_a < 0 ? "text-red-500" : "text-muted-foreground"}`}>
                      residuo {res_a}/{a.qta}
                    </span>
                    <Input type="number" min="0" max={a.qta} value={qtaOf(idx, a.codice) || ""} onChange={(e) => setQta(idx, a.codice, e.target.value)}
                      className="h-7 w-16 text-right text-xs" data-testid={`piano-qta-${idx}-${a.codice}`} />
                  </div>
                );
              })}
            </div>

            {rout && (
              <div className="rounded bg-muted/40 p-1.5 text-[11px] text-muted-foreground" data-testid={`piano-esito-${idx}`}>
                {rout.det?.calcolabile || isRitiro ? (
                  <span>{rout.det?.peso_tassato_kg ?? rout.det?.peso_reale_kg ?? 0} kg · {rout.det?.pallet_totali ?? 0} bancali · {rout.det?.n_colli ?? 0} colli · {isRitiro ? <span className="font-semibold text-foreground">Ritiro (a carico cliente)</span> : <span className="font-semibold text-foreground">{eurN(rout.totale)}</span>}</span>
                ) : (
                  <span className="text-red-500">Trasporto non calcolabile (peso/volume mancanti)</span>
                )}
                {(rout.atp_warn || []).length > 0 && (
                  <div className="mt-1 flex items-start gap-1 text-amber-600 dark:text-amber-400" data-testid={`piano-atp-warn-${idx}`}>
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>Attenzione: {rout.atp_warn.map((w) => w.codice).join(", ")} potrebbero non essere disponibili entro questa data.</span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {res && (
        <div className="space-y-1 border-t border-input pt-2 text-xs">
          {!alloc?.ok && (
            <div className="flex items-start gap-1 text-red-500" data-testid="piano-alloc-error">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                Allocazione incompleta.
                {alloc?.residui?.length ? ` Da assegnare: ${alloc.residui.map((r) => `${r.codice} (${r.mancano})`).join(", ")}.` : ""}
                {alloc?.eccessi?.length ? ` In eccesso: ${alloc.eccessi.map((r) => `${r.codice} (+${r.extra})`).join(", ")}.` : ""}
              </span>
            </div>
          )}
          {!isRitiro && missing.length > 0 && (
            <div className="flex items-start gap-1 text-red-500" data-testid="piano-missing-fisici">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>Peso/volume mancanti a listino per: {missing.join(", ")}. Completa i dati nel Listino per calcolare il trasporto.</span>
            </div>
          )}
          <div className="flex items-center justify-between font-semibold text-foreground" data-testid="piano-totale">
            <span className="flex items-center gap-1.5">{isRitiro ? "Ritiro in fabbrica" : <><Truck className="h-3.5 w-3.5" /> Totale trasporto ({plan.length} spedizioni)</>}</span>
            <span>{isRitiro ? "€ 0,00 (a carico cliente)" : eurN(res.totale)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
